import { useState, useEffect, useCallback, useMemo } from "react";
import { Select, DatePicker } from "antd";
import dayjs from "dayjs";
import { Sliders, Save, RefreshCw, Pencil, X } from "lucide-react";
import useApiMethods from "../../lib/useApiMethods";
import { ATTRIBUTE_VALUES_URL } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";

/**
 * Renders whatever fields an admin has defined for this customer — the form is
 * built from the API response, so adding a field in Settings makes it appear
 * here with no code change.
 */
const CustomerAttributesPanel = ({ customerId, customerName }) => {
    const { fetch, create } = useApiMethods();

    const [groups,  setGroups]  = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving,  setSaving]  = useState(false);
    const [editing, setEditing] = useState(false);
    const [draft,   setDraft]   = useState({});

    const load = useCallback(() => {
        if (!customerId) return;
        setLoading(true);
        fetch(`${ATTRIBUTE_VALUES_URL}?customer_id=${customerId}`)
            .then(res => {
                if (res?.status === "success") {
                    setGroups(res.groups || []);
                    // Seed the draft from stored answers so editing starts from truth
                    const seeded = {};
                    (res.data || []).forEach(a => {
                        seeded[a.slug] = a.value ?? (a.takes_many ? [] : "");
                    });
                    setDraft(seeded);
                } else {
                    ToastMsg("error", res?.message || "Failed to load details");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load details"))
            .finally(() => setLoading(false));
    }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const allAttrs = useMemo(
        () => groups.flatMap(g => g.attributes || []), [groups]);

    const setField = (slug, value) => setDraft(d => ({ ...d, [slug]: value }));

    const save = () => {
        // Send only fields the user actually filled, so blank optional fields
        // don't create empty rows for thousands of leads.
        const payload = {};
        allAttrs.forEach(a => {
            // Retired fields are shown but not collected any more. Submitting
            // one is rejected server-side, and validation is all-or-nothing —
            // it would fail the whole save over a field nobody edited.
            if (a.is_retired) return;
            const v = draft[a.slug];
            const empty = v === "" || v === null || v === undefined ||
                          (Array.isArray(v) && v.length === 0);
            if (!empty || a.has_value) payload[a.slug] = v;
        });
        if (!Object.keys(payload).length) { setEditing(false); return; }

        setSaving(true);
        create(ATTRIBUTE_VALUES_URL, { customer_id: customerId, values: payload })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", "Details saved");
                    setEditing(false);
                    load();
                } else {
                    ToastMsg("error", res?.message || "Failed to save details");
                }
            })
            .catch(() => ToastMsg("error", "Failed to save details"))
            .finally(() => setSaving(false));
    };

    // ── read-only display of one answer ──
    const displayValue = (a) => {
        const v = a.value;
        const blank = v === null || v === undefined || v === "" ||
                      (Array.isArray(v) && v.length === 0);
        if (blank) return <span className="text-gray-300 dark:text-gray-600">—</span>;

        if (a.input_type === "boolean") return v ? "Yes" : "No";
        if (a.input_type === "date")    return dayjs(v).format("DD MMM YYYY");
        if (a.input_type === "money")   return "₹" + Number(v).toLocaleString("en-IN");

        if (a.takes_options) {
            const chosen = Array.isArray(v) ? v : [v];
            const labels = chosen.map(sel => {
                const opt = (a.options || []).find(o =>
                    a.reference_source ? o.id === sel : o.slug === sel);
                return opt ? opt.name : String(sel);
            });
            return (
                <span className="flex flex-wrap gap-1">
                    {labels.map((l, i) => (
                        <span key={i} className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px]">
                            {l}
                        </span>
                    ))}
                </span>
            );
        }
        return <>{String(v)}{a.unit ? <span className="text-gray-400 ml-0.5">{a.unit}</span> : null}</>;
    };

    // ── editable control for one attribute, chosen by its input type ──
    const editor = (a) => {
        const v = draft[a.slug];
        const base = "w-full text-xs px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:ring-1 focus:ring-indigo-400";

        if (a.takes_options) {
            const options = (a.options || []).map(o => ({
                value: a.reference_source ? o.id : o.slug,
                label: o.price ? `${o.name} — ₹${Number(o.price).toLocaleString("en-IN")}` : o.name,
            }));
            return (
                <Select
                    mode={a.takes_many ? "multiple" : undefined}
                    allowClear
                    value={a.takes_many ? (v || []) : (v || undefined)}
                    onChange={val => setField(a.slug, val ?? (a.takes_many ? [] : ""))}
                    options={options}
                    className="w-full"
                    size="small"
                    placeholder={options.length ? `Select ${a.name}` : "No options configured yet"}
                    notFoundContent="Nothing set up yet — add options in Settings"
                />
            );
        }
        if (a.input_type === "boolean") {
            return (
                <Select value={v === "" ? undefined : !!v}
                    onChange={val => setField(a.slug, val)}
                    className="w-full" size="small" placeholder="Select"
                    options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]} />
            );
        }
        if (a.input_type === "date") {
            return (
                <DatePicker value={v ? dayjs(v) : null}
                    onChange={d => setField(a.slug, d ? d.format("YYYY-MM-DD") : "")}
                    className="w-full" size="small" format="DD MMM YYYY" />
            );
        }
        if (a.input_type === "textarea") {
            return <textarea rows={2} value={v || ""} onChange={e => setField(a.slug, e.target.value)}
                className={cn(base, "resize-none")} />;
        }
        if (["number", "decimal", "money"].includes(a.input_type)) {
            return (
                <div className="flex items-center gap-1">
                    <input type="number" min="0"
                        step={a.input_type === "number" ? "1" : "any"}
                        value={v ?? ""} onChange={e => setField(a.slug, e.target.value)}
                        className={base} />
                    {a.unit && <span className="text-[10px] text-gray-400 whitespace-nowrap">{a.unit}</span>}
                </div>
            );
        }
        return <input type="text" value={v || ""} onChange={e => setField(a.slug, e.target.value)}
            className={base} />;
    };

    if (!customerId) return null;

    if (loading && !groups.length) {
        return (
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex items-center gap-2">
                <RefreshCw className="h-3 w-3 animate-spin text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Loading details…</span>
            </div>
        );
    }

    // No attributes defined for this customer type — say how to add them rather
    // than rendering an empty box.
    if (!allAttrs.length) {
        return (
            <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-3">
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300 flex items-center gap-1.5">
                    <Sliders className="h-3.5 w-3.5" /> Additional Details
                </p>
                <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
                    No custom fields are set up yet. Add them under
                    Settings → Custom Fields and they appear here automatically.
                </p>
            </div>
        );
    }

    // Retired fields are excluded from the ratio: they cannot be filled in, so
    // counting them would leave a denominator nobody can ever reach.
    const liveAttrs   = allAttrs.filter(a => !a.is_retired);
    const filledCount = liveAttrs.filter(a => a.has_value).length;

    return (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
            <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/40">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-700 dark:text-gray-200">
                    <Sliders className="h-3.5 w-3.5 text-indigo-500" />
                    Additional Details
                    <span className="font-normal text-gray-400">
                        {filledCount}/{liveAttrs.length} filled
                    </span>
                </span>
                {editing ? (
                    <div className="flex items-center gap-1">
                        <button disabled={saving} onClick={save}
                            className="h-6 px-2 rounded-md text-[10px] font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white flex items-center gap-1">
                            <Save className="h-3 w-3" /> {saving ? "Saving…" : "Save"}
                        </button>
                        <button onClick={() => { setEditing(false); load(); }}
                            className="h-6 w-6 rounded-md border border-gray-300 dark:border-gray-600 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-center">
                            <X className="h-3 w-3" />
                        </button>
                    </div>
                ) : (
                    <button onClick={() => setEditing(true)}
                        className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline flex items-center gap-1">
                        <Pencil className="h-3 w-3" /> Edit
                    </button>
                )}
            </div>

            <div className="p-3 space-y-3">
                {groups.map(group => (
                    <div key={group.group}>
                        <p className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-2">
                            {group.group}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-6 gap-y-2">
                            {(group.attributes || []).map(a => (
                                <div key={a.slug} className={cn(
                                    editing && !a.is_retired
                                        ? "space-y-1"
                                        : "flex items-start justify-between gap-3"
                                )}>
                                    <label className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0"
                                        title={a.is_retired
                                            ? "No longer collected — kept because this lead already answered"
                                            : (a.help_text || undefined)}>
                                        {a.name}
                                        {a.is_required && !a.is_retired && <span className="text-red-500 ml-0.5">*</span>}
                                        {a.is_retired && (
                                            <span className="ml-1 text-[9px] px-1 py-px rounded bg-gray-100 dark:bg-gray-700 text-gray-400">
                                                archived
                                            </span>
                                        )}
                                    </label>
                                    {editing && !a.is_retired ? editor(a) : (
                                        <span className={cn(
                                            "text-xs font-medium text-right",
                                            a.is_retired
                                                ? "text-gray-400 dark:text-gray-500"
                                                : "text-gray-800 dark:text-gray-200"
                                        )}>
                                            {displayValue(a)}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CustomerAttributesPanel;
